import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'

import { Button } from '@ui/components/Button'
import { ArrowIcon } from '@ui/icons/Arrow.icon'

import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { BasePaths } from '@utils/path'

import { useLogout } from '@hooks/useLogout'

import {
  TestStatusTemplateActions,
  TestStatusTemplateBody,
  TestStatusTemplateContent,
  TestStatusTemplateInnerWrapper,
} from '@shared/templates/TestStatusTemplate/TestStatusTemplate'

import { SignupFormWrapper } from '../../Components/SignupFormWrapper'
import { SignupSections, SignupStep } from '../../Components/SignupStep'

enum FailReason {
  Test = 'test',
  Signup = 'signup',
}

export const FinancialInfoFailPage: React.FC = () => {
  const { failReason = FailReason.Test } = useParams<{ failReason?: FailReason }>()

  const { account } = useAccountReadContext()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { logout } = useLogout()
  const entity = useSessionEntity()

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href)

      logout()
      navigate('/logout')
    }

    window.history.pushState(null, '', window.location.href)

    window.onpopstate = handlePopState

    return () => {
      window.onpopstate = null
    }
  }, [logout, navigate])

  const content: Record<FailReason, { title: string; description: string[] }> = {
    [FailReason.Test]: {
      title: t('Profile.Trading Experience Test Failed!'),
      description: [
        t(
          'Profile.You may fully use Client Area functions and open a live trading account after satisfying appropriateness test requirements. Please note that live trading account opening and Client Area functions will be frozen until you satisfy appropriateness test requirements.'
        ),
      ],
    },
    [FailReason.Signup]:
      entity === TickmillCompaniesEnum.TICKMILL_EU
        ? {
            title: t('Application Denied'),
            description: [t('Sign up.Based on your responses to the Appropriateness Test')],
          }
        : {
            title: t('Sign up.Your registration has been unsuccessful') + '!',
            description: [
              t('Sign up.From the information you have provided, we do not think', {
                email: 'support@tickmill.co.uk',
              }),
            ],
          },
  }

  const clearSessionAndLogout = async () => {
    logout()
    navigate('/logout')
  }

  const navigateToTest = () => {
    navigate(BasePaths.TakeAppTest)
  }

  const renderButton = () => {
    switch (failReason) {
      case FailReason.Signup:
        return (
          <Button.Primary size='lg' onClick={clearSessionAndLogout}>
            {t('Profile.Logout')}
          </Button.Primary>
        )
      case FailReason.Test:
        return (
          <Button.Primary
            size='lg'
            type='submit'
            iconRight={ArrowIcon.Right}
            onClick={navigateToTest}
          >
            {t('Next')}
          </Button.Primary>
        )
    }
  }

  return (
    <SignupFormWrapper label={t('Sign up.Sign up')} hideCloseIcon>
      <TestStatusTemplateInnerWrapper>
        <SignupStep
          accountTypeId={account?.type.id}
          isFailed
          section={SignupSections.FinancialInfo}
        />
        <TestStatusTemplateBody>
          <TestStatusTemplateContent
            title={content[failReason].title}
            text={content[failReason].description}
          />
          <TestStatusTemplateActions>{renderButton()}</TestStatusTemplateActions>
        </TestStatusTemplateBody>
      </TestStatusTemplateInnerWrapper>
    </SignupFormWrapper>
  )
}
